import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import MDBox from "admin/components/MDBox";
import MDTypography from "admin/components/MDTypography";
import DashboardLayout from "admin/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "admin/examples/Navbars/DashboardNavbar";
import ReportsBarChart from "admin/examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "admin/examples/Charts/LineCharts/ReportsLineChart";
import axios from "axios";

const API_URL = process.env.REACT_APP_APIHOST || "http://localhost:3033";

const dark = {
  bg: "#1a1a1a",
  card: "#222",
  border: "rgba(255,255,255,0.06)",
  text: "#aaa",
  textLight: "#666",
  white: "#fff",
  gold: "#C29717",
  red: "#e55",
};

const sx = {
  card: {
    background: dark.card,
    border: `1px solid ${dark.border}`,
    borderRadius: "2px",
    p: 2.5,
  },
  kpi: {
    background: dark.bg,
    border: `1px solid ${dark.border}`,
    borderRadius: "2px",
    p: 2.5,
    position: "relative",
    overflow: "hidden",
  },
};

function KpiCard({ icon, label, value, sub, subColor }) {
  return (
    <MDBox sx={sx.kpi}>
      <MDBox display="flex" alignItems="center" gap={1} mb={1}>
        <Icon sx={{ color: dark.gold, fontSize: "18px !important" }}>{icon}</Icon>
        <MDTypography sx={{ color: dark.textLight, fontSize: "11px", textTransform: "uppercase", letterSpacing: "1.5px" }}>
          {label}
        </MDTypography>
      </MDBox>
      <MDTypography sx={{ color: dark.white, fontSize: "28px", fontWeight: 400, lineHeight: 1.2, mb: 0.5 }}>
        {value}
      </MDTypography>
      {sub && (
        <MDTypography sx={{ color: subColor || dark.text, fontSize: "12px" }}>
          {sub}
        </MDTypography>
      )}
    </MDBox>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trackStats, setTrackStats] = useState(null);

  useEffect(() => {
    fetchStats(); fetchTrackStats();
  }, []);

  const fetchTrackStats = async () => {
    try {
      const res = await axios.get(API_URL + '/track/stats');
      if (res.data.status) setTrackStats(res.data.data);
    } catch(e) {}
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/events?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.status && res.data.data) {
        setStats(computeStats(res.data.data));
      }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const computeStats = (events) => {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
    const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;

    let totalDeaths = 0, thisMonthEvents = 0, lastMonthEvents = 0;
    let thisMonthDeaths = 0, lastMonthDeaths = 0;
    const countries = new Set();
    const byViolence = {}, byCountry = {};
    const monthlyMap = {}, dailyMap = {};
    const published = events.filter((e) => e.status === "Published");

    events.forEach((e) => {
      const deaths = (e.sideADeath || 0) + (e.sideBDeath || 0) + (e.deathCivilians || 0) + (e.deathUnknown || 0);
      totalDeaths += deaths;
      if (e.country) { countries.add(e.country); byCountry[e.country] = (byCountry[e.country] || 0) + 1; }
      if (e.typeOfViolence) byViolence[e.typeOfViolence] = (byViolence[e.typeOfViolence] || 0) + 1;

      if (e.startDate) {
        const d = new Date(e.startDate);
        const m = d.getMonth(), y = d.getFullYear();
        if (m === thisMonth && y === thisYear) { thisMonthEvents++; thisMonthDeaths += deaths; }
        if (m === lastMonth && y === lastMonthYear) { lastMonthEvents++; lastMonthDeaths += deaths; }
        const mk = `${y}-${String(m + 1).padStart(2, "0")}`;
        monthlyMap[mk] = (monthlyMap[mk] || 0) + 1;
        const dk = d.toISOString().split("T")[0];
        dailyMap[dk] = (dailyMap[dk] || 0) + 1;
      }
    });

    const monthLabels = [], monthValues = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(thisYear, thisMonth - i, 1);
      monthLabels.push(d.toLocaleString("en", { month: "short" }));
      monthValues.push(monthlyMap[`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`] || 0);
    }

    const dayLabels = [], dayValues = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      dayLabels.push(d.toLocaleString("en", { day: "numeric", month: "short" }));
      dayValues.push(dailyMap[d.toISOString().split("T")[0]] || 0);
    }

    const sortedCountries = Object.entries(byCountry).sort((a, b) => b[1] - a[1]).slice(0, 8);
    const evtPct = lastMonthEvents > 0 ? Math.round(((thisMonthEvents - lastMonthEvents) / lastMonthEvents) * 100) : (thisMonthEvents > 0 ? 100 : 0);
    const deathPct = lastMonthDeaths > 0 ? Math.round(((thisMonthDeaths - lastMonthDeaths) / lastMonthDeaths) * 100) : (thisMonthDeaths > 0 ? 100 : 0);

    return {
      totalEvents: events.length, publishedEvents: published.length, totalDeaths, countriesAffected: countries.size,
      thisMonthEvents, evtPct, deathPct,
      topViolence: Object.entries(byViolence).sort((a, b) => b[1] - a[1]).slice(0, 10),
      recentEvents: published.sort((a, b) => new Date(b.startDate) - new Date(a.startDate)).slice(0, 8),
      monthlyChart: { labels: monthLabels, datasets: { label: "Events", data: monthValues } },
      dailyChart: { labels: dayLabels, datasets: { label: "Events", data: dayValues } },
      countryChart: { labels: sortedCountries.map((c) => c[0]), datasets: { label: "Events", data: sortedCountries.map((c) => c[1]) } },
    };
  };

  if (loading || !stats) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3} display="flex" justifyContent="center">
          <MDTypography color="text">Loading...</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} sx={{ "& .MuiCard-root": { background: dark.card, border: `1px solid ${dark.border}`, boxShadow: "none" } }}>

        {/* KPIs */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} lg={3}>
            <KpiCard icon="crisis_alert" label="Total Events" value={stats.totalEvents}
              sub={`${stats.evtPct >= 0 ? "+" : ""}${stats.evtPct}% vs last month`}
              subColor={stats.evtPct > 0 ? dark.red : "#4caf50"} />
          </Grid>
          <Grid item xs={6} lg={3}>
            <KpiCard icon="public" label="Countries" value={stats.countriesAffected}
              sub={`${stats.thisMonthEvents} events this month`} />
          </Grid>
          <Grid item xs={6} lg={3}>
            <KpiCard icon="person_off" label="Casualties" value={stats.totalDeaths.toLocaleString()}
              sub={`${stats.deathPct >= 0 ? "+" : ""}${stats.deathPct}% vs last month`}
              subColor={stats.deathPct > 0 ? dark.red : "#4caf50"} />
          </Grid>
          <Grid item xs={6} lg={3}>
            <KpiCard icon="check_circle" label="Published" value={stats.publishedEvents}
              sub={`${stats.totalEvents - stats.publishedEvents} unpublished`} />
          </Grid>
        </Grid>

        {/* Traffic KPIs */}
        {trackStats && (
          <Grid container spacing={2} mb={2}>
            <Grid item xs={6} lg={3}>
              <KpiCard icon="visibility" label="Today views" value={trackStats.today?.views || 0} sub={trackStats.today?.sessions + ' sessions'} />
            </Grid>
            <Grid item xs={6} lg={3}>
              <KpiCard icon="people" label="Month sessions" value={trackStats.month?.sessions || 0} sub={trackStats.month?.views + ' page views'} />
            </Grid>
          </Grid>
        )}

        {/* Charts */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={4}>
            <ReportsBarChart color="dark" title="By Country" description="Top affected" date="" chart={stats.countryChart} />
          </Grid>
          <Grid item xs={12} md={4}>
            <ReportsLineChart color="dark" title="Monthly Trend" description="Last 6 months" date="" chart={stats.monthlyChart} />
          </Grid>
          <Grid item xs={12} md={4}>
            <ReportsLineChart color="dark" title="Daily Activity" description="Last 14 days" date="" chart={stats.dailyChart} />
          </Grid>
        </Grid>

        {/* Bottom */}
        <Grid container spacing={2}>
          {/* Violence types */}
          <Grid item xs={12} md={5}>
            <MDBox sx={sx.card}>
              <MDTypography sx={{ color: dark.gold, fontSize: "10px", textTransform: "uppercase", letterSpacing: "1.5px", mb: 2 }}>
                Events by Type
              </MDTypography>
              {stats.topViolence.map(([type, count]) => (
                <MDBox key={type} display="flex" justifyContent="space-between" alignItems="center"
                  sx={{ py: 1, borderBottom: `1px solid ${dark.border}`, "&:last-child": { borderBottom: "none" } }}>
                  <MDTypography sx={{ color: dark.text, fontSize: "13px" }}>{type}</MDTypography>
                  <MDTypography sx={{ color: dark.white, fontSize: "13px", fontWeight: 500 }}>{count}</MDTypography>
                </MDBox>
              ))}
            </MDBox>
          </Grid>

          {/* Recent events */}
          <Grid item xs={12} md={7}>
            <MDBox sx={sx.card}>
              <MDTypography sx={{ color: dark.gold, fontSize: "10px", textTransform: "uppercase", letterSpacing: "1.5px", mb: 2 }}>
                Recent Events
              </MDTypography>
              {stats.recentEvents.map((event) => {
                const deaths = (event.sideADeath || 0) + (event.sideBDeath || 0) + (event.deathCivilians || 0) + (event.deathUnknown || 0);
                return (
                  <MDBox key={event._id} display="flex" justifyContent="space-between" alignItems="flex-start"
                    sx={{ py: 1.2, borderBottom: `1px solid ${dark.border}`, "&:last-child": { borderBottom: "none" } }}>
                    <MDBox>
                      <MDTypography sx={{ color: dark.white, fontSize: "13px", lineHeight: 1.4 }}>
                        {event.conflictName || "Untitled"}
                      </MDTypography>
                      <MDTypography sx={{ color: dark.textLight, fontSize: "11px" }}>
                        {event.country} — {event.startDate ? new Date(event.startDate).toLocaleDateString("en", { day: "numeric", month: "short", year: "numeric" }) : "N/A"}
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" alignItems="center" gap={1} flexShrink={0}>
                      {deaths > 0 && (
                        <MDTypography sx={{ color: dark.red, fontSize: "11px" }}>{deaths}</MDTypography>
                      )}
                      <MDTypography sx={{
                        color: dark.gold, fontSize: "9px", textTransform: "uppercase",
                        letterSpacing: "0.5px", background: "rgba(194,151,23,0.1)",
                        border: "1px solid rgba(194,151,23,0.2)", borderRadius: "2px",
                        px: 1, py: 0.3, whiteSpace: "nowrap",
                      }}>
                        {event.typeOfViolence || "—"}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                );
              })}
            </MDBox>
          </Grid>
        </Grid>

      </MDBox>


    </DashboardLayout>
  );
}

export default Dashboard;
